import React, { useState, useEffect } from 'react';
import { 
  LayoutDashboard, CreditCard, Users, Wallet, 
  LogOut, ShieldCheck, ChevronRight, Bell,
  User, Lock, Settings, X, KeyRound, Eye, EyeOff, Menu, MessageSquare
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { agenciesAPI } from '../utils/api';

const AgencyLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPass, setShowPass] = useState({ old: false, new: false, confirm: false });
  const [loading, setLoading] = useState(false);
  const [agencyInfo, setAgencyInfo] = useState({ name: 'Loading...', level: 'Partner' });

  useEffect(() => {
    const fetchAgencyInfo = async () => {
      try {
        const res = await agenciesAPI.getDashboardStats();
        if (res.data.agency_name) {
          setAgencyInfo({ 
            name: res.data.agency_name, 
            level: 'Level 4 Partner' 
          });
        }
      } catch (err) {
        console.error("Failed to fetch agency info");
      }
    };
    fetchAgencyInfo();
  }, []);

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: <LayoutDashboard size={20} />, path: '/agency/dashboard' },
    { id: 'recharge', label: 'Recharge Coins', icon: <CreditCard size={20} />, path: '/agency/recharge' },
    { id: 'hosts', label: 'My Hosts', icon: <Users size={20} />, path: '/agency/hosts' },
    { id: 'wallet', label: 'My Wallet', icon: <Wallet size={20} />, path: '/agency/wallet' },
    { id: 'support', label: 'Help & Complaints', icon: <MessageSquare size={20} />, path: '/agency/support' },
  ];

  const handleLogout = () => {
    sessionStorage.clear();
    window.location.href = '/agency/login';
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert("New passwords do not match!");
      return;
    }
    setLoading(true);
    try {
      await agenciesAPI.changePassword({ 
        oldPassword: passwordData.oldPassword, 
        newPassword: passwordData.newPassword 
      });
      setShowPasswordModal(false);
      setPasswordData({ oldPassword: '', newPassword: '', confirmPassword: '' });
      alert('Password updated successfully!');
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-[#020617] text-slate-200 font-['Outfit'] overflow-x-hidden">
      {/* Sidebar - Desktop: Sticky, Mobile: Drawer */}
      <aside className={`
        fixed md:sticky top-0 inset-y-0 left-0 z-50 h-screen w-72 bg-[#0f172a]/95 md:bg-[#0f172a]/50 border-r border-white/5 backdrop-blur-xl flex flex-col justify-between transition-transform duration-300
        ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
      `}>
        <div>
          <div className="p-6 flex items-center justify-between border-b border-white/5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-tr from-pink-600 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-pink-600/20">
                <ShieldCheck size={22} className="text-white" />
              </div>
              <div>
                <h1 className="text-base font-black text-white tracking-tight uppercase">XOXO <span className="text-pink-500">AGENCY</span></h1>
                <p className="text-[9px] text-slate-500 font-bold tracking-widest uppercase">Verified Partner</p>
              </div>
            </div>
            <button 
              onClick={() => setIsSidebarOpen(false)} 
              className="md:hidden p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 cursor-pointer"
            >
              <X size={20} />
            </button>
          </div>

          <nav className="px-4 py-6 space-y-2">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  navigate(item.path);
                  setIsSidebarOpen(false);
                }}
                className={`w-full flex items-center gap-4 px-6 py-4 rounded-[20px] transition-all duration-300 group cursor-pointer ${
                  location.pathname === item.path
                  ? 'bg-gradient-to-r from-pink-600/10 to-indigo-600/10 border border-pink-500/20 text-white shadow-[0_0_20px_rgba(219,39,119,0.1)]'
                  : 'text-slate-500 hover:text-slate-200 hover:bg-white/5'
                }`}
              >
                <div className={`transition-transform duration-300 group-hover:scale-110 ${
                  location.pathname === item.path ? 'text-pink-500' : ''
                }`}>
                  {item.icon}
                </div>
                <span className="text-sm font-bold tracking-wide">{item.label}</span>
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6 border-t border-white/5">
          <button 
            onClick={handleLogout}
            className="w-full flex items-center gap-4 px-6 py-4 rounded-[20px] bg-red-500/5 border border-red-500/10 text-red-500 hover:bg-red-500 hover:text-white transition-all group cursor-pointer"
          >
            <LogOut size={20} className="group-hover:-translate-x-1 transition-transform" />
            <span className="text-sm font-bold">Logout Session</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-w-0">
        {/* Top Header */}
        <header className="h-16 md:h-20 border-b border-white/5 bg-[#020617]/80 backdrop-blur-md flex items-center justify-between px-4 sm:px-8 sticky top-0 z-40 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="md:hidden p-2 rounded-xl bg-white/5 text-slate-300 hover:text-white cursor-pointer shrink-0"
              aria-label="Toggle navigation"
            >
              <Menu size={20} />
            </button>
            <div className="min-w-0">
              <h2 className="text-base sm:text-xl md:text-2xl font-black text-white capitalize truncate">{location.pathname.split('/').pop().replace('-', ' ') || 'Dashboard'}</h2>
              <div className="hidden sm:flex items-center gap-2 mt-0.5">
                <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></div>
                <span className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">Live Database Connected</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 sm:gap-6 shrink-0">
            <div className="relative p-2.5 sm:p-3 bg-white/5 rounded-2xl border border-white/10 group cursor-pointer hover:bg-white/10 transition-all">
              <Bell size={18} className="text-slate-400 group-hover:text-pink-500 transition-colors" />
              <div className="absolute top-2.5 right-2.5 w-2 h-2 bg-pink-600 rounded-full border-2 border-[#020617]"></div>
            </div>

            <div className="h-8 w-[1px] bg-white/10 mx-1 hidden sm:block"></div>

            <div className="relative">
              <div 
                onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                className="flex items-center gap-4 cursor-pointer group hover:bg-white/5 p-2 rounded-2xl transition-all"
              >
                <div className="text-right hidden sm:block">
                  <p className="text-sm font-black text-white group-hover:text-pink-500 transition-colors">{agencyInfo.name}</p>
                  <p className="text-[10px] text-indigo-400 font-bold uppercase tracking-widest">{agencyInfo.level}</p>
                </div>
                <div className="relative">
                  <div className="absolute -inset-1 bg-gradient-to-r from-pink-600 to-indigo-600 rounded-full blur opacity-25 group-hover:opacity-75 transition duration-500"></div>
                  <img 
                    src="https://i.pravatar.cc/150?u=agency" 
                    className="relative w-12 h-12 rounded-full border-2 border-[#020617] object-cover"
                    alt="profile"
                  />
                </div>
              </div>

              {/* Profile Dropdown */}
              {showProfileDropdown && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowProfileDropdown(false)}></div>
                  <div className="absolute right-0 mt-4 w-64 bg-[#0f172a] border border-white/10 rounded-3xl shadow-2xl p-3 z-20 animate-in fade-in zoom-in-95 duration-200 overflow-hidden">
                    <div className="p-4 border-b border-white/5 mb-2 bg-white/5">
                       <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Agency Account</p>
                       <p className="text-sm font-black text-white truncate">{agencyInfo.name}</p>
                    </div>
                    
                    <button 
                      onClick={() => { setShowPasswordModal(true); setShowProfileDropdown(false); }}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-white/5 text-slate-300 hover:text-white transition-all text-sm font-bold"
                    >
                      <KeyRound size={18} className="text-pink-500" />
                      Change Password
                    </button>
                    
                    <button className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-white/5 text-slate-300 hover:text-white transition-all text-sm font-bold">
                      <Settings size={18} className="text-indigo-500" />
                      Account Settings
                    </button>
                    
                    <div className="h-[1px] bg-white/5 my-2"></div>
                    
                    <button 
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-red-500/10 text-red-500 transition-all text-sm font-bold"
                    >
                      <LogOut size={18} />
                      Sign Out
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-10 flex-1">
          {children}
        </div>
      </main>

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-300">
           <div className="bg-[#0f172a] border border-white/10 rounded-[40px] p-8 md:p-10 max-w-md w-full shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-pink-600 to-indigo-600"></div>
              
              <div className="flex justify-between items-center mb-8">
                 <div className="w-12 h-12 bg-pink-500/10 rounded-2xl flex items-center justify-center text-pink-500">
                    <KeyRound size={24} />
                 </div>
                 <button onClick={() => setShowPasswordModal(false)} className="p-2 bg-white/5 rounded-xl text-slate-500 hover:text-white">
                    <X size={20} />
                 </button>
              </div>

              <div className="mb-8 text-center sm:text-left">
                 <h3 className="text-2xl font-black text-white tracking-tight">Security Settings</h3>
                 <p className="text-slate-500 text-sm mt-2 font-bold uppercase tracking-widest">Update Agency Password</p>
              </div>

              <form onSubmit={handleChangePassword} className="space-y-6">
                 {/* Old Password */}
                 <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-widest ml-1">Current Password</label>
                    <div className="relative">
                      <input 
                        type={showPass.old ? "text" : "password"}
                        required
                        className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white focus:border-pink-500 outline-none transition-all pr-14"
                        placeholder="••••••••"
                        value={passwordData.oldPassword}
                        onChange={(e) => setPasswordData({...passwordData, oldPassword: e.target.value})}
                      />
                      <button 
                        type="button" 
                        onClick={() => setShowPass({...showPass, old: !showPass.old})}
                        className="absolute right-6 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
                      >
                        {showPass.old ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                 </div>

                 <div className="h-[1px] bg-white/5 my-2"></div>

                 {/* New Password */}
                 <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-widest ml-1">New Password</label>
                    <div className="relative">
                      <input 
                        type={showPass.new ? "text" : "password"}
                        required
                        minLength={6}
                        className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white focus:border-pink-500 outline-none transition-all pr-14"
                        placeholder="••••••••"
                        value={passwordData.newPassword}
                        onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                      />
                      <button 
                        type="button" 
                        onClick={() => setShowPass({...showPass, new: !showPass.new})}
                        className="absolute right-6 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
                      >
                        {showPass.new ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                 </div>

                 {/* Confirm Password */}
                 <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-widest ml-1">Confirm New Password</label>
                    <div className="relative">
                      <input 
                        type={showPass.confirm ? "text" : "password"}
                        required
                        className={`w-full bg-white/5 border rounded-2xl px-6 py-4 text-white outline-none transition-all pr-14 ${
                          passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword 
                          ? 'border-red-500/50' 
                          : 'border-white/10 focus:border-pink-500'
                        }`}
                        placeholder="••••••••"
                        value={passwordData.confirmPassword}
                        onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                      />
                      <button 
                        type="button" 
                        onClick={() => setShowPass({...showPass, confirm: !showPass.confirm})}
                        className="absolute right-6 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
                      >
                        {showPass.confirm ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    {passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword && (
                      <p className="text-[10px] text-red-500 font-bold uppercase tracking-widest ml-1">Passwords do not match</p>
                    )}
                 </div>

                 <button 
                   type="submit"
                   disabled={loading || passwordData.newPassword !== passwordData.confirmPassword || !passwordData.oldPassword}
                   className="w-full py-5 bg-gradient-to-r from-pink-600 to-indigo-600 text-white rounded-2xl font-black uppercase tracking-widest hover:shadow-[0_0_25px_rgba(219,39,119,0.4)] transition-all disabled:opacity-50"
                 >
                    {loading ? 'Updating Security...' : 'Confirm Update'}
                 </button>
              </form>
           </div>
        </div>
      )}
    </div>
  );
};

export default AgencyLayout;
